import { Link } from "@inertiajs/react"
import { CheckCircle2, RefreshCw } from "lucide-react"
import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import type { TriageSectionState } from "@/types/home-triage"

import { HomeTriageCardGrid } from "./home-triage-card-grid"
import { HomeTriageSkeleton } from "./home-triage-skeleton"

/**
 * OKS-706 — Reusable home-triage section shell.
 *
 * The foundation primitive both the Candidates-to-review section (this
 * ticket) and the sibling submissions (OKS-707) + agency (OKS-708)
 * sections consume. Renders a labelled `<section>` with:
 *   - a header row: title + live count `Badge` + optional right-aligned
 *     "view all" link (shown when the queue exceeds the display cap);
 *   - a 5-state body (cold_start / empty / loading / error / populated).
 *
 * It is deliberately DATA-SHAPE-AGNOSTIC — the count/state/viewAllUrl are
 * passed in and the card grid + empty/cold-start bodies are supplied by
 * the consumer as slots. Consumers own their card component + query; the
 * shell owns the chrome, the state matrix, and accessibility.
 *
 * Accessibility (UX Spec §6): the section carries an `aria-label`; the
 * count badge is `aria-live="polite"` so decrements are announced after
 * an action reload. The stale cue + all state affordances are icon+text,
 * never colour alone (owned by the card + empty-state primitives).
 */
export interface HomeTriageSectionProps {
  /** Accessible + visible section title (e.g. "Candidates to review"). */
  title: string
  /**
   * OKS-951 — optional one-line subtitle under the header row.
   *
   * Optional because only « À relancer » specifies one (UX Spec §4); the three
   * sibling sections pass nothing and are unchanged. It renders BELOW the
   * header row rather than beside the title so it never competes with the count
   * badge and the "view all" link for horizontal space at 375px.
   */
  subtitle?: string
  /** Current section state — `loading` renders the skeleton. */
  state: TriageSectionState
  /** Count for the header badge (cards currently in the section). */
  count: number
  /** Non-null → render a "view all" link (queue exceeds display cap). */
  viewAllUrl?: string | null
  /** Retry handler for the error state (partial reload of the section). */
  onRetry?: () => void
  /** Whether a Retry round-trip is currently in flight. */
  isRetrying?: boolean
  /**
   * OKS-923 — the rows on screen are the last-known ones and a round-trip is
   * settling. A MODIFIER orthogonal to `state`, not a sixth state: the section
   * is still `populated`, merely one round-trip behind. (`state` is the server
   * contract — `TriageSection.state` is `Exclude<TriageSectionState, "loading">`
   * — and this marking is knowable only client-side, at the `<Deferred>`
   * fallback that knows the prop is currently absent. `isRetrying` is the
   * shipped precedent for the shape.)
   */
  isRefreshing?: boolean
  /** Cold-start body slot (brand has never added the underlying record). */
  renderColdStart?: () => ReactNode
  /** Empty body slot ("all caught up" — has records, none pending). */
  renderEmpty?: () => ReactNode
  /** Error body slot; falls back to a built-in error card + Retry. */
  renderError?: () => ReactNode
  /** Populated body — the card grid (consumer supplies the cards). */
  children?: ReactNode
  /**
   * **OKS-953 (PRD R7)** — when `state === "empty"`, render the ENTIRE section
   * as a single ~44px confirming line (check icon + title + `À jour` badge)
   * instead of a header plus a full-height empty panel.
   *
   * 🔴 OPT-IN, and deliberately not the default. This shell is shared by SIX
   * sections and R7's argument is about the FIVE candidate segments: a brand
   * with a clean pipeline sees five empty segments, and five full-height
   * "nothing here" panels read as broken while five one-line confirmations
   * read as done. The submissions section is not part of that stack and keeps
   * its panel.
   *
   * ⚠️ `cold_start` never collapses, whatever this is set to. That state is
   * ONBOARDING — it is the only guidance a brand that has never used the
   * feature will see, and collapsing it would hide the thing it exists to say.
   *
   * The collapsed row carries the consumer's OWN empty testid (via
   * `emptyTestId`) so shipped assertions keep a target rather than simply
   * going red.
   */
  collapseWhenEmpty?: boolean
  /**
   * **OKS-953** — the testid the collapsed row carries, so an existing
   * `<section>-empty` assertion survives the collapse. Ignored unless
   * `collapseWhenEmpty` is set.
   */
  emptyTestId?: string
  /** Stable testid root for QA (default `home-triage-section`). */
  testId?: string
  className?: string
}

export function HomeTriageSection({
  title,
  subtitle,
  state,
  count,
  viewAllUrl,
  onRetry,
  isRetrying = false,
  isRefreshing = false,
  renderColdStart,
  renderEmpty,
  renderError,
  children,
  collapseWhenEmpty = false,
  emptyTestId,
  testId = "home-triage-section",
  className,
}: HomeTriageSectionProps) {
  const { t } = useTranslation()

  // OKS-953 (PRD R7) — the collapsed resting state. Returns EARLY: the whole
  // section becomes one row, so there is no header, no count badge and no body
  // to render. `data-state="empty"` and the section testid are preserved so
  // state-based selectors keep working; `data-collapsed` is what distinguishes
  // this from a section that merely rendered a short empty panel.
  //
  // Deliberately NOT animated: a resting state is not an event (UX Spec §4).
  if (collapseWhenEmpty && state === "empty") {
    return (
      <section
        aria-label={t("candidates.home_all_clear.aria", { section: title })}
        data-testid={testId}
        data-state="empty"
        data-collapsed="true"
        className={cn(
          "flex min-h-11 items-center gap-2 rounded-lg border border-dashed px-3",
          className,
        )}
      >
        {/* Icon + TEXT + badge — colour and icon are never the sole signal
            (UX Spec §6); the `À jour` badge carries the meaning in words. */}
        <CheckCircle2
          className="text-muted-foreground size-4 shrink-0"
          aria-hidden="true"
        />
        <h2 className="text-muted-foreground text-sm font-medium">{title}</h2>
        <Badge
          variant="secondary"
          className="ml-auto"
          data-testid={emptyTestId ?? `${testId}-empty`}
        >
          {t("candidates.home_all_clear.badge")}
        </Badge>
      </section>
    )
  }

  return (
    <section
      aria-label={title}
      data-testid={testId}
      data-state={state}
      // OKS-923 — ALWAYS emitted, "true" or "false", never omitted when false.
      // An absent attribute makes `[data-refreshing="true"] → count 0` pass
      // when the component failed to render at all, so the negative assertion
      // would be unfalsifiable.
      data-refreshing={isRefreshing ? "true" : "false"}
      // Marked busy so the dim is not visual-only (UX Spec §6).
      aria-busy={isRefreshing}
      className={cn("flex flex-col gap-4", className)}
    >
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
          {/* Count badge — announced on change so SR users hear decrements.
              While refreshing it HOLDS its last value, so nothing changes and
              nothing is announced: the badge must never announce a number it
              hasn't received (UX Spec §4). */}
          <Badge
            variant="secondary"
            aria-live="polite"
            data-testid={`${testId}-count`}
          >
            {count}
          </Badge>
          {/*
            OKS-923 — the refreshing cue. Icon + TEXT, so it is not
            colour-only and stays perceptible at 320px where a single card
            fills the viewport and there is no neighbouring row to compare
            against (UX Spec §2/§6). Reads as "catching up" rather than
            "broken" or "disabled" — no spinner over the content, and the rows
            stay interactive.
          */}
          {isRefreshing && (
            <span
              data-testid={`${testId}-refreshing`}
              className="text-muted-foreground inline-flex items-center gap-1 text-xs font-medium whitespace-nowrap"
            >
              <RefreshCw className="size-3" aria-hidden="true" />
              {t("home_triage.refreshing")}
            </span>
          )}
        </div>
        {viewAllUrl && (
          <Link
            href={viewAllUrl}
            className="text-primary text-sm font-medium hover:underline"
            data-testid={`${testId}-view-all`}
          >
            {t("home_triage.view_all")}
          </Link>
        )}
      </header>

      {/*
        OKS-951 — optional subtitle. Rendered only when a consumer supplies one
        (« À relancer » is the only section that specifies one, UX Spec §4), so
        the three siblings are byte-identical without it.
      */}
      {subtitle && (
        <p
          data-testid={`${testId}-subtitle`}
          className="text-muted-foreground -mt-2 text-sm"
        >
          {subtitle}
        </p>
      )}

      {/*
        OKS-923 — reduced emphasis on the retained rows. Deliberately NOT a
        transition: the dim and its removal are both immediate, so nothing
        fades out after the data has already landed (UX Spec §4). Dimming only
        — the rows are not removed, replaced or disabled, because the whole
        point is that the user keeps working in them.
      */}
      <div className={cn(isRefreshing && "opacity-60")}>{renderBody()}</div>
    </section>
  )

  function renderBody() {
    if (state === "loading") return <HomeTriageSkeleton />

    if (state === "error") {
      if (renderError) return <>{renderError()}</>
      return (
        <div
          role="alert"
          data-testid={`${testId}-error`}
          className="border-border flex flex-col items-center gap-3 rounded-lg border border-dashed p-6 text-center"
        >
          <p className="text-muted-foreground text-sm">
            {t("home_triage.error.message")}
          </p>
          {onRetry && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRetry}
              disabled={isRetrying}
              aria-disabled={isRetrying}
              data-testid={`${testId}-retry`}
            >
              <RefreshCw className="size-4" aria-hidden="true" />
              {t("home_triage.error.retry")}
            </Button>
          )}
        </div>
      )
    }

    if (state === "cold_start") return <>{renderColdStart?.()}</>
    if (state === "empty") return <>{renderEmpty?.()}</>

    // populated
    return <HomeTriageCardGrid>{children}</HomeTriageCardGrid>
  }
}
